import { Target, Eye, Award, Users, Lightbulb, Shield, Clock, TrendingUp } from 'lucide-react'

const missionVision = [
  {
    icon: Target,
    title: '企业使命',
    content: '专注于新能源汽车充电领域，整合产业链上下游资源，提供一站式服务；以技术为驱动，以生态聚合为手段，以全域赋能为目标，推动产业发展。',
    color: 'from-neon-cyan to-neon-blue',
  },
  {
    icon: Eye,
    title: '企业愿景',
    content: '成为新能源汽车充电产业最值得信赖的生态服务伙伴，构建互联互通的智慧充电网络，推动绿色出行生态的可持续发展。',
    color: 'from-neon-green to-emerald-400',
  },
]

const coreValues = [
  {
    icon: Lightbulb,
    title: '技术创新',
    description: '持续投入研发，以技术创新驱动服务升级',
  },
  {
    icon: Users,
    title: '客户至上',
    description: '以客户需求为导向，提供定制化解决方案',
  },
  {
    icon: Shield,
    title: '品质保障',
    description: '严格的质量管理体系，确保服务可靠性',
  },
  {
    icon: Award,
    title: '合作共赢',
    description: '与合作伙伴共建生态，实现互利共赢',
  },
]

const milestones = [
  {
    year: '2024',
    title: '公司成立',
    description: '北京壹电利科技有限公司正式成立，专注新能源汽车充电产业生态服务',
  },
  {
    year: '2024',
    title: '平台发布',
    description: '自主研发"壹电利充"聚合充电运营管理平台正式上线',
  },
  {
    year: '2024',
    title: '战略合作',
    description: '与高德地图、阿里云、腾讯等头部企业达成战略合作',
  },
  {
    year: '2025',
    title: '规模扩张',
    description: '服务覆盖多个省市，累计服务充电运营商10+',
  },
]

const highlights = [
  { icon: Clock, value: '7×24', label: '小时服务' },
  { icon: Users, value: '10+', label: '成功案例' },
  { icon: Award, value: '5', label: '专业资质' },
  { icon: TrendingUp, value: '30%', label: '成本降低' },
]

export default function About() {
  return (
    <div className="overflow-hidden bg-dark">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 radial-glow opacity-50" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-6">
              <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-sm font-medium">关于我们</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              关于<span className="text-gradient">壹电利</span>
            </h1>
            <p className="text-lg text-white/50 leading-relaxed">
              壹电利（ONEEC）创立于2024年，专注新能源领域创新服务，聚焦新能源汽车充电产业生态构建。作为行业垂直整合服务商，公司以自主研发的"壹电利充"聚合充电运营管理平台为核心，打造兼具互联互通与直联设备管控能力的智能系统。
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 md:py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {highlights.map((item, index) => (
              <div
                key={item.label}
                className="glass-card p-6 text-center hover:border-neon-green/30 transition-all"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <item.icon className="w-8 h-8 text-neon-green mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{item.value}</div>
                <div className="text-white/50 text-sm">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {missionVision.map((item, index) => (
              <div
                key={item.title}
                className="glass-card p-8 md:p-10 hover:border-neon-green/30 transition-all"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 neon-glow`}>
                  <item.icon className="w-7 h-7 text-dark" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{item.title}</h2>
                <p className="text-white/50 leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Award className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">核心价值观</span>
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              我们的<span className="text-gradient">价值理念</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {coreValues.map((value, index) => (
              <div
                key={value.title}
                className="glass-card p-6 text-center hover:border-neon-green/30 transition-all group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <value.icon className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-white/50 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-4">
              <Clock className="w-4 h-4 text-neon-green" />
              <span className="text-neon-green text-sm font-medium">发展历程</span>
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              壹电利<span className="text-gradient">成长之路</span>
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-gradient-to-b from-neon-green via-neon-cyan to-transparent hidden md:block" />
            
            <div className="space-y-8 md:space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.title}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="glass-card p-6 hover:border-neon-green/30 transition-all inline-block">
                      <span className="text-neon-green font-bold text-lg">{milestone.year}</span>
                      <h3 className="text-xl font-bold text-white mt-2 mb-2">{milestone.title}</h3>
                      <p className="text-white/50 text-sm">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="w-4 h-4 rounded-full bg-neon-green neon-glow hidden md:block" />
                  
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
